import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { formatDateTime } from "@/utils/formatting";
import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import { Tag } from "lucide-react";

// Define the structure based on change_log table
type ChangeLogEntry = {
  id: number;
  version_id: number;
  provider_id: number;
  region_id: number;
  entity_type: string;
  entity_id: number;
  field_name: string;
  action_type: 'CREATE' | 'UPDATE' | 'DELETE';
  old_value: string | null;
  new_value: string | null;
  created_at: string;
  updated_at: string;
};

interface TagChangesTableProps {
  changes?: ChangeLogEntry[];
  isLoading: boolean;
}

export function TagChangesTable({ changes, isLoading }: TagChangesTableProps) {
  const [tagNames, setTagNames] = useState<Record<number, string>>({});

  // Filter to only show channel_tags entity_type
  const tagChanges = changes?.filter(change =>
    change.entity_type === 'channel_tags'
  ) || [];

  // Get unique tag IDs
  const tagIds = [...new Set(tagChanges.map(c => c.entity_id))];

  // Fetch tag names from database and extract from deleted entity data
  useEffect(() => {
    const fetchTagNames = async () => {
      if (tagIds.length === 0) return;

      // First try to get names from the channel_tags table for existing tags
      const { data, error } = await (supabase
        .from('channel_tags' as any)
        .select('id, name')
        .in('id', tagIds) as any);

      const nameMap: Record<number, string> = {};

      // Add names for existing tags
      if (!error && data) {
        data.forEach((tag: any) => {
          nameMap[tag.id] = tag.name;
        });
      }

      // For tags not found in the database (deleted), extract names from change log old_value
      const missingTagIds = tagIds.filter(id => !nameMap[id]);
      if (missingTagIds.length > 0) {
        missingTagIds.forEach(tagId => {
          // Find a change entry for this tag that has old_value with entity data
          const entityChange = tagChanges.find(change =>
            change.entity_id === tagId &&
            change.field_name === 'entity' &&
            change.old_value
          );

          if (entityChange?.old_value) {
            try {
              const entityData = JSON.parse(entityChange.old_value);
              nameMap[tagId] = entityData.name || `Tag #${tagId}`;
            } catch (error) {
              console.error('Failed to parse entity data for tag:', tagId, error);
              nameMap[tagId] = `Tag #${tagId}`;
            }
          } else {
            nameMap[tagId] = `Tag #${tagId}`;
          }
        });
      }

      setTagNames(nameMap);
    };

    fetchTagNames();
  }, [tagIds.join(','), tagChanges]);

  // Group changes by tag and then by actual action type
  const groupedByTag = tagChanges.reduce((acc, change) => {
    const tagId = change.entity_id;

    if (!acc[tagId]) {
      acc[tagId] = {
        tagId,
        tagName: tagNames[tagId] || `Tag #${tagId}`,
        createFields: {},
        updateFields: {},
        deleteFields: {},
        createTimestamp: null,
        updateTimestamp: null,
        deleteTimestamp: null,
        latestTimestamp: change.created_at
      };
    }

    // Track latest timestamp for this tag
    if (new Date(change.created_at) > new Date(acc[tagId].latestTimestamp)) {
      acc[tagId].latestTimestamp = change.created_at;
    }

    // Handle entity-level operations (whole tag created/deleted)
    if (change.field_name === 'entity') {
      if (change.old_value === null && change.new_value !== null) {
        // Entire tag was created
        acc[tagId].isEntityCreated = true;
        acc[tagId].entityCreatedAt = change.created_at;
        // Parse entity data for display
        try {
          const entityData = JSON.parse(change.new_value);
          acc[tagId].entityData = entityData;
        } catch (error) {
          console.error('Failed to parse entity creation data:', error);
        }
      } else if (change.old_value !== null && change.new_value === null) {
        // Entire tag was deleted
        acc[tagId].isEntityDeleted = true;
        acc[tagId].entityDeletedAt = change.created_at;
        // Parse entity data for display
        try {
          const entityData = JSON.parse(change.old_value);
          acc[tagId].entityData = entityData;
        } catch (error) {
          console.error('Failed to parse entity deletion data:', error);
        }
      }
    } else {
      // Handle field-level changes
      if (change.old_value === null && change.new_value !== null) {
        // Field was created (added)
        acc[tagId].createFields[change.field_name] = {
          value: change.new_value
        };
        if (!acc[tagId].createTimestamp || new Date(change.created_at) > new Date(acc[tagId].createTimestamp!)) {
          acc[tagId].createTimestamp = change.created_at;
        }
      } else if (change.old_value !== null && change.new_value === null) {
        // Field was deleted (removed)
        acc[tagId].deleteFields[change.field_name] = {
          value: change.old_value
        };
        if (!acc[tagId].deleteTimestamp || new Date(change.created_at) > new Date(acc[tagId].deleteTimestamp!)) {
          acc[tagId].deleteTimestamp = change.created_at;
        }
      } else if (change.old_value !== null && change.new_value !== null) {
        // Field was updated
        if (!acc[tagId].updateFields[change.field_name]) {
          acc[tagId].updateFields[change.field_name] = {
            oldValue: change.old_value,
            newValue: change.new_value
          };
        } else {
          // If field was already updated, keep the original old value but update the new value
          acc[tagId].updateFields[change.field_name].newValue = change.new_value;
        }
        if (!acc[tagId].updateTimestamp || new Date(change.created_at) > new Date(acc[tagId].updateTimestamp!)) {
          acc[tagId].updateTimestamp = change.created_at;
        }
      }
    }

    return acc;
  }, {} as Record<number, {
    tagId: number;
    tagName: string;
    createFields: Record<string, { value: string }>;
    updateFields: Record<string, { oldValue: string; newValue: string }>;
    deleteFields: Record<string, { value: string }>;
    createTimestamp: string | null;
    updateTimestamp: string | null;
    deleteTimestamp: string | null;
    latestTimestamp: string;
    isEntityCreated?: boolean;
    isEntityDeleted?: boolean;
    entityCreatedAt?: string;
    entityDeletedAt?: string;
    entityData?: any;
  }>);

  if (isLoading) return <div>Loading...</div>;

  // Convert to array and sort
  const sortedTags = Object.values(groupedByTag).sort((a, b) =>
    new Date(b.latestTimestamp).getTime() - new Date(a.latestTimestamp).getTime()
  );

  const renderFieldValue = (
    actionType: 'CREATE' | 'UPDATE' | 'DELETE',
    _fieldName: string,
    fieldData: any
  ) => {
    if (!fieldData) return <span className="text-muted-foreground">-</span>;

    // Regular field handling
    if (actionType === 'CREATE') {
      return (
        <div className="text-xs text-green-600 font-medium">
          {fieldData.value || 'null'}
        </div>
      );
    } else if (actionType === 'UPDATE') {
      return (
        <div className="space-y-1">
          <div className="text-xs text-red-600 line-through">
            {fieldData.oldValue || 'null'}
          </div>
          <div className="text-xs text-green-600 font-medium">
            {fieldData.newValue || 'null'}
          </div>
        </div>
      );
    } else if (actionType === 'DELETE') {
      return (
        <div className="text-xs text-red-600 line-through">
          {fieldData.value || 'null'}
        </div>
      );
    }

    return <span className="text-muted-foreground">-</span>;
  };

  if (sortedTags.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
        <Tag className="h-12 w-12 mb-4" />
        <p className="text-lg font-medium">No tag changes found</p>
        <p className="text-sm">There are no tracked changes for tags in this version</p>
      </div>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="font-semibold">Tag Name</TableHead>
          <TableHead className="font-semibold">Action</TableHead>
          <TableHead className="font-semibold">Name</TableHead>
          <TableHead className="font-semibold">Description</TableHead>
          <TableHead className="font-semibold">Timestamp</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {sortedTags.map((tag) => {
          // Build array of actions to display
          const actions = [];

          // Add ENTITY_CREATED row if the entire tag was created
          if (tag.isEntityCreated) {
            actions.push({
              type: 'ENTITY_CREATED' as const,
              fields: {},
              timestamp: tag.entityCreatedAt,
              entityData: tag.entityData
            });
          }

          // Add ENTITY_DELETED row if the entire tag was deleted
          if (tag.isEntityDeleted) {
            actions.push({
              type: 'ENTITY_DELETED' as const,
              fields: {},
              timestamp: tag.entityDeletedAt,
              entityData: tag.entityData
            });
          }

          // Add CREATE row if there are created fields
          if (Object.keys(tag.createFields).length > 0) {
            actions.push({
              type: 'CREATE' as const,
              fields: tag.createFields,
              timestamp: tag.createTimestamp
            });
          }

          // Add UPDATE row if there are updated fields
          if (Object.keys(tag.updateFields).length > 0) {
            actions.push({
              type: 'UPDATE' as const,
              fields: tag.updateFields,
              timestamp: tag.updateTimestamp
            });
          }

          // Add DELETE row if there are deleted fields
          if (Object.keys(tag.deleteFields).length > 0) {
            actions.push({
              type: 'DELETE' as const,
              fields: tag.deleteFields,
              timestamp: tag.deleteTimestamp
            });
          }

          if (actions.length === 0) return null;

          return actions.map((action, index) => {
            const isFirstRow = index === 0;

            return (
              <TableRow key={`${tag.tagId}-${action.type}`} className="hover:bg-muted/50">
                {/* Tag Name - merged cell for first row only */}
                {isFirstRow && (
                  <TableCell
                    rowSpan={actions.length}
                    className="font-medium align-top border-r"
                  >
                    <div className="flex items-center space-x-2">
                      <Tag className="h-4 w-4 text-muted-foreground" />
                      <span className="font-semibold">{tag.tagName}</span>
                    </div>
                  </TableCell>
                )}

                {/* Action */}
                <TableCell>
                  <Badge className={
                    action.type === 'ENTITY_CREATED' ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-100 font-bold" :
                    action.type === 'ENTITY_DELETED' ? "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100 font-bold" :
                    action.type === 'CREATE' ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100" :
                    action.type === 'UPDATE' ? "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-100" :
                    "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-100"
                  }>
                    {action.type === 'ENTITY_CREATED' ? 'TAG CREATED' :
                     action.type === 'ENTITY_DELETED' ? 'TAG DELETED' :
                     action.type}
                  </Badge>
                </TableCell>

                {/* Name */}
                <TableCell>
                  {action.type === 'ENTITY_CREATED' || action.type === 'ENTITY_DELETED'
                    ? <div className={`text-xs font-medium ${action.type === 'ENTITY_DELETED' ? 'text-red-600 line-through' : 'text-green-600'}`}>
                        {action.entityData?.name || '-'}
                      </div>
                    : renderFieldValue(action.type, 'name', action.fields['name'])}
                </TableCell>

                {/* Description */}
                <TableCell>
                  {action.type === 'ENTITY_CREATED' || action.type === 'ENTITY_DELETED'
                    ? <div className={`text-xs ${action.type === 'ENTITY_DELETED' ? 'text-red-600 line-through' : 'text-green-600'} max-w-xs truncate`}>
                        {action.entityData?.description || '-'}
                      </div>
                    : renderFieldValue(action.type, 'description', action.fields['description'])}
                </TableCell>

                {/* Timestamp */}
                <TableCell>
                  <div className="text-xs">{action.timestamp ? formatDateTime(action.timestamp) : '-'}</div>
                </TableCell>
              </TableRow>
            );
          });
        })}
      </TableBody>
    </Table>
  );
}
